<template>
  <v-container class="fill-height d-flex justify-center align-center">
    <v-sheet
      class="border-opacity-25 overflow-hidden border-thin rounded-lg pa-5"
      max-width="600"
      width="100%">
      <v-row v-if="asset.hasUrl() && !isAdding" justify="center">
        <v-col cols="12" md="12">
          <div v-if="asset.fileType() == 'image'" class="d-flex justify-center align-center">
            <v-img :src="asset.url">
              <template #sources>
                <source :srcset="asset?.webp_url" />
              </template>
            </v-img>
          </div>
          <div
            v-if="asset.fileType() == 'video'"
            class="video_player d-flex flex-column align-center justify-center">
            <vue3-video-player
              :core="asset.hasHlsUrl() ? HLSCore : undefined"
              :src="asset.hasHlsUrl() ? asset.hls_url : asset.url"></vue3-video-player>
          </div>
          <div class="mx-auto mt-1">
            <v-row>
              <v-col v-if="isModal">
                <v-btn
                  color="secondar"
                  block
                  class="rounded-lg mb-8"
                  elevation="2"
                  @click="emit('assetCanceled')"
                  >Cancel</v-btn
                >
              </v-col>
              <v-col v-if="!disableUpload">
                <v-btn
                  color="primary"
                  block
                  class="rounded-lg mb-8"
                  elevation="2"
                  @click="toggleUpload">
                  Change
                </v-btn>
              </v-col>
            </v-row>
            <v-row v-if="allowDownload">
              <v-col>
                <a
                  :href="asset.url"
                  download
                  class="text-decoration-none text-primary text-body-2 font-weight-medium">
                  <v-btn block color="primary" class="rounded-lg mb-8" elevation="2">
                    Download
                  </v-btn>
                </a>
              </v-col>
            </v-row>
          </div>
        </v-col>
      </v-row>
      <v-row v-else-if="!uploadDisabled">
        <v-col cols="12" md="12">
          <v-form>
            <v-file-input v-model="file" label="Choose a file" :accept="fileType"></v-file-input>
            <v-row>
              <v-col cols="12" md="6" sm="6">
                <v-btn
                  color="secondary"
                  block
                  class="rounded-lg mb-8"
                  elevation="2"
                  @click="handleCancel"
                  >Cancel</v-btn
                >
              </v-col>
              <v-col cols="12" md="6" sm="6">
                <v-btn
                  :disabled="!file"
                  color="primary"
                  block
                  class="rounded-lg mb-8"
                  elevation="2"
                  @click="uploadAsset"
                  >Upload</v-btn
                >
              </v-col>
            </v-row>
            <v-progress-linear
              v-if="uploadProgress > 0"
              v-model="uploadProgress"
              height="10"
              color="blue"></v-progress-linear>
          </v-form>
        </v-col>
      </v-row>
      <v-row v-if="asset.hasUrl() && !isAdding">
        <v-col cols="12" class="text-center">
          <p class="text-caption text-secondary">
            Uploaded on: {{ formatDisplayDate(asset.created_on, true) }}
          </p>
        </v-col>
      </v-row>
    </v-sheet>
  </v-container>
</template>
<script setup lang="ts">
import { createProgressHandler } from '@/controllers';
import { AssetDTO, IAssetDTO } from '@/dtos';
import { computed, ref, watch } from 'vue';
import { formatDisplayDate } from '@/helpers';
import HLSCore from '@cloudgeek/playcore-hls';
import { useAuthStore } from '@/stores';

const emit = defineEmits(['assetCanceled', 'assetSaved', 'snack']);
interface Props {
  asset: IAssetDTO | undefined;
  type: 'image' | 'video' | 'pdf' | 'any';
  onUpload?: (file: File, updateProgress: (progress: number) => void) => Promise<IAssetDTO>;
  isModal?: boolean;
  disableUpload?: boolean;
  allowDownload?: boolean;
}
const props = defineProps<Props>();
const asset = ref<IAssetDTO>(new AssetDTO());
watch(
  () => props.asset,
  () => {
    asset.value = new AssetDTO(props.asset);
  },
  { immediate: true },
);
const fileType = computed(() => {
  if (props.type === 'image') return 'image/*';
  if (props.type === 'video') return 'video/*';
  if (props.type === 'pdf') return 'application/pdf';
  return '*';
});
const authStore = useAuthStore();
const isAdding = ref<boolean>(false);
const file = ref<File>();
const uploadProgress = ref<number>(0);
const uploadCompleted = ref<boolean>(false);
const isModal = computed(() => props.isModal ?? true);
const uploadDisabled = computed(() => props.disableUpload ?? false);
const allowDownload = computed(() => (props.allowDownload || authStore.isAdmin) ?? false);
const toggleUpload = async () => {
  file.value = undefined;
  uploadProgress.value = 0;
  uploadCompleted.value = false;
  isAdding.value = !isAdding.value;
};
const handleCancel = () => {
  if (isAdding.value == true) {
    isAdding.value = false;
  } else {
    emit('assetCanceled');
  }
};

const uploadAsset = async () => {
  if (!file.value || !props.onUpload) {
    emit('snack', 'File', 'error', 'No file selected');
    return;
  }

  try {
    const progress = createProgressHandler(uploadProgress, uploadCompleted);
    const ret = await props.onUpload(file.value, progress);
    asset.value = new AssetDTO(ret);
    emit('snack', 'File', 'success:update');
    setTimeout(() => {
      isAdding.value = false;
      emit('assetSaved', asset.value);
    }, 1000);
  } catch (error: unknown) {
    const msg = error as string;
    emit('snack', 'File', 'error', `Upload failed: ${msg}`);
  }
};
</script>
